import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { Send, ArrowLeft, CheckCheck, Check, Paperclip, Image, Mic, X, File, Smile, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useNavigate } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Notification sound
const messageSound = new Audio('/sounds/message.mp3');
messageSound.volume = 0.5;

const playNotificationSound = () => {
  try {
    messageSound.currentTime = 0;
    messageSound.play().catch(err => console.log('Audio play failed:', err));
  } catch (err) {
    console.log('Audio error:', err);
  }
};

export const Chat = () => {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [uploading, setUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    connectSocket();

    return () => {
      if (socketRef.current) {
        // Leave match room before disconnecting
        socketRef.current.emit('leave_match', {
          match_id: matchId
        });
        socketRef.current.disconnect();
      }
      
      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [matchId]);

  // Separate effect for polling - only when disconnected
  useEffect(() => {
    let pollInterval;
    if (connectionStatus !== 'connected') {
      pollInterval = setInterval(() => {
        fetchMessagesWithNotification();
      }, 3000);
    }
    
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [connectionStatus, matchId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch messages with notification for new ones (used by polling)
  const fetchMessagesWithNotification = async () => {
    try {
      const response = await api.get(`/messages/${matchId}`);
      const newMessages = response.data;
      
      setMessages(prev => {
        // Check if there are new messages from others
        const prevIds = new Set(prev.map(m => m.id));
        const newFromOthers = newMessages.filter(m => 
          !prevIds.has(m.id) && m.sender_id !== user.id
        );
        
        if (newFromOthers.length > 0) {
          // Play sound and show notification for new messages
          playNotificationSound();
          toast.success(`${newFromOthers.length} new message${newFromOthers.length > 1 ? 's' : ''}!`, {
            duration: 2000,
          });
        }
        
        return newMessages;
      });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await api.get(`/messages/${matchId}`);
      setMessages(response.data);
      
      const matchResponse = await api.get('/matches');
      const currentMatch = matchResponse.data.find(m => m.match_id === matchId);
      if (currentMatch) {
        setOtherUser(currentMatch.user);
      }
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const connectSocket = () => {
    // Don't reconnect if already connected or connecting
    if (socketRef.current?.connected) {
      return;
    }
    
    console.log('Connecting to WebSocket...');
    
    // Build WebSocket URL properly
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    
    socketRef.current = io(BACKEND_URL, {
      path: '/api/socket.io',  // Custom path that routes correctly through ingress
      transports: ['polling', 'websocket'],  // Start with polling for better compatibility
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: 3,  // Limit reconnection attempts
      timeout: 10000
    });

    socketRef.current.on('connect', () => {
      console.log('✓ WebSocket connected');
      setConnectionStatus('connected');
      
      // Authenticate
      socketRef.current.emit('authenticate', {
        user_id: user.id
      });
      
      // Join match room
      socketRef.current.emit('join_match', {
        match_id: matchId,
        user_id: user.id
      });
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('✗ WebSocket disconnected:', reason);
      setConnectionStatus('disconnected');
    });

    socketRef.current.on('connection_confirmed', (data) => {
      console.log('✓ Connection confirmed:', data);
    });

    socketRef.current.on('new_message', (message) => {
      console.log('📨 New message received:', message);
      
      // Only process messages from other users (not our own)
      if (message.sender_id !== user.id) {
        // Add message to state
        setMessages(prev => {
          // Check if message already exists to avoid duplicates
          if (prev.some(m => m.id === message.id)) {
            return prev;
          }
          return [...prev, message];
        });
        
        // Play notification sound
        playNotificationSound();
        
        // Show toast notification
        toast.success('New message received!', {
          duration: 2000,
        });
        
        // Send read receipt if chat is open
        if (socketRef.current?.connected) {
          socketRef.current.emit('message_read', {
            match_id: matchId,
            message_id: message.id
          });
        }
      }
    });

    socketRef.current.on('user_typing', (data) => {
      if (data.user_id !== user.id) {
        setIsTyping(data.is_typing);
        
        // Auto-hide typing indicator after 3 seconds
        if (data.is_typing) {
          setTimeout(() => setIsTyping(false), 3000);
        }
      }
    });

    socketRef.current.on('user_joined_chat', (data) => {
      if (data.user_id !== user.id) {
        setIsOnline(true);
      }
    });

    socketRef.current.on('user_left_chat', (data) => {
      if (data.user_id !== user.id) {
        setIsOnline(false);
      }
    });

    socketRef.current.on('user_online', (data) => {
      if (otherUser && data.user_id === otherUser.id) {
        setIsOnline(true);
      }
    });

    socketRef.current.on('user_offline', (data) => {
      if (otherUser && data.user_id === otherUser.id) {
        setIsOnline(false);
      }
    });

    socketRef.current.on('message_sent', (data) => {
      console.log('✓ Message delivered:', data);
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      setConnectionStatus('error');
    });
    
    socketRef.current.on('reconnect_failed', () => {
      console.log('WebSocket reconnection failed, using polling fallback');
      setConnectionStatus('disconnected');
    });
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    
    // Emit typing start event only if connected
    if (socketRef.current?.connected && e.target.value) {
      socketRef.current.emit('typing_start', {
        match_id: matchId
      });
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to emit typing stop
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current.emit('typing_stop', {
          match_id: matchId
        });
      }, 1000);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    
    // Stop typing indicator if WebSocket is connected
    if (socketRef.current && connectionStatus === 'connected') {
      socketRef.current.emit('typing_stop', {
        match_id: matchId
      });
    }

    try {
      const response = await api.post('/messages', {
        match_id: matchId,
        content: messageContent
      });
      
      // Add message to local state immediately
      setMessages(prev => [...prev, response.data]);
      
      // Emit through WebSocket for real-time delivery if connected
      if (socketRef.current && connectionStatus === 'connected') {
        socketRef.current.emit('send_message', {
          match_id: matchId,
          message: response.data
        });
      }
      
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(messageContent); // Restore message on error
    }
  };

  // File upload handler
  const handleFileUpload = async (file, fileType) => {
    if (!file) {
      console.log('No file to upload');
      return;
    }
    
    console.log('Uploading file:', file.name, file.size, file.type);
    
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size exceeds 5MB limit');
      return;
    }
    
    setUploading(true);
    setShowAttachMenu(false);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('Sending upload request...');
      const uploadResponse = await api.post('/upload/chat-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      console.log('Upload response:', uploadResponse.data);
      
      if (uploadResponse.data.success) {
        // Send message with file
        console.log('Sending message with file...');
        const response = await api.post('/messages', {
          match_id: matchId,
          content: fileType === 'audio' ? 'Voice message' : file.name,
          message_type: fileType,
          file_url: uploadResponse.data.file_url,
          file_name: uploadResponse.data.file_name,
          file_size: uploadResponse.data.file_size
        });
        
        console.log('Message sent:', response.data);
        
        setMessages(prev => [...prev, response.data]);
        
        if (socketRef.current && connectionStatus === 'connected') {
          socketRef.current.emit('send_message', {
            match_id: matchId,
            message: response.data
          });
        }
        
        toast.success(`${fileType === 'audio' ? 'Voice message' : fileType.charAt(0).toUpperCase() + fileType.slice(1)} sent!`);
      } else {
        console.error('Upload failed:', uploadResponse.data);
        toast.error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  // Audio blob upload handler (avoids File constructor issues)
  const uploadAudioBlob = async (blob, fileName, mimeType) => {
    if (!blob || blob.size === 0) {
      console.log('No audio blob to upload');
      return;
    }
    
    console.log('Uploading audio blob:', fileName, blob.size, mimeType);
    
    if (blob.size > MAX_FILE_SIZE) {
      toast.error('Audio file size exceeds 5MB limit');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      // Append blob directly with filename - this is more compatible than using File constructor
      formData.append('file', blob, fileName);
      
      console.log('Sending audio upload request...');
      const uploadResponse = await api.post('/upload/chat-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      console.log('Audio upload response:', uploadResponse.data);
      
      if (uploadResponse.data.success) {
        // Send message with audio file
        console.log('Sending voice message...');
        const response = await api.post('/messages', {
          match_id: matchId,
          content: 'Voice message',
          message_type: 'audio',
          file_url: uploadResponse.data.file_url,
          file_name: uploadResponse.data.file_name,
          file_size: uploadResponse.data.file_size
        });
        
        console.log('Voice message sent:', response.data);
        
        setMessages(prev => [...prev, response.data]);
        
        if (socketRef.current && connectionStatus === 'connected') {
          socketRef.current.emit('send_message', {
            match_id: matchId,
            message: response.data
          });
        }
        
        toast.success('Voice message sent!');
      } else {
        console.error('Audio upload failed:', uploadResponse.data);
        toast.error('Failed to send voice message');
      }
    } catch (error) {
      console.error('Audio upload error:', error);
      toast.error('Failed to send voice message: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  // Image selection handler
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, 'image');
    }
  };

  // File selection handler
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, 'file');
    }
  };

  // Audio recording functions
  const startRecording = async () => {
    try {
      console.log('Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('Microphone access granted');
      
      // Check supported MIME types
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4') 
          ? 'audio/mp4' 
          : 'audio/ogg';
      
      console.log('Using MIME type:', mimeType);
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        console.log('Audio data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        console.log('Recording stopped, chunks:', audioChunksRef.current.length);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Check if we have data and should upload
        if (audioChunksRef.current.length > 0 && mediaRecorderRef.current?.shouldUpload !== false) {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          console.log('Created audio blob:', audioBlob.size, 'bytes');
          
          const extension = mimeType.split('/')[1];
          const fileName = `voice_${Date.now()}.${extension}`;
          
          // Upload using FormData with blob directly (more compatible than File constructor)
          console.log('Uploading audio:', fileName, audioBlob.size);
          await uploadAudioBlob(audioBlob, fileName, mimeType);
        } else {
          console.log('Recording cancelled or no data');
        }
        
        // Clear chunks
        audioChunksRef.current = [];
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        toast.error('Recording error');
      };
      
      // Start recording
      mediaRecorder.start(100);
      mediaRecorderRef.current.shouldUpload = true;
      
      setIsRecording(true);
      setRecordingTime(0);
      setShowAttachMenu(false);
      setShowEmojiPicker(false);
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.info('Recording started...', { duration: 1500 });
      console.log('Recording started');
      
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Could not access microphone. Please allow microphone permission.');
    }
  };

  const stopRecording = () => {
    console.log('Stop recording called, isRecording:', isRecording);
    if (mediaRecorderRef.current && isRecording) {
      console.log('Stopping recorder to send...');
      mediaRecorderRef.current.shouldUpload = true;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
      setRecordingTime(0);
    }
  };

  const cancelRecording = () => {
    console.log('Cancel recording called');
    if (mediaRecorderRef.current && isRecording) {
      console.log('Cancelling recording...');
      mediaRecorderRef.current.shouldUpload = false;
      mediaRecorderRef.current.stop();
      audioChunksRef.current = [];
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
      setRecordingTime(0);
      toast.info('Recording cancelled');
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render message content based on type (WhatsApp style)
  const renderMessageContent = (message) => {
    const fileUrl = message.file_url ? `${BACKEND_URL}${message.file_url}` : null;
    
    switch (message.message_type) {
      case 'image':
        return (
          <div className="-mx-1 -mt-0.5 mb-1">
            <img 
              src={fileUrl} 
              alt={message.file_name || 'Image'} 
              className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              style={{ maxHeight: '300px', minWidth: '200px' }}
              onClick={() => window.open(fileUrl, '_blank')}
            />
            {message.content && message.content !== message.file_name && (
              <p className="mt-1 text-[14.2px]">{message.content}</p>
            )}
          </div>
        );
      
      case 'audio':
        return (
          <div className="flex items-center gap-2 min-w-[240px] py-1">
            <div className="w-10 h-10 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] rounded-full flex items-center justify-center flex-shrink-0">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <audio controls className="w-full h-8" style={{ minWidth: '160px' }}>
                <source src={fileUrl} type="audio/webm" />
                <source src={fileUrl} type="audio/mp3" />
              </audio>
            </div>
          </div>
        );
      
      case 'file':
        return (
          <a 
            href={fileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-2 bg-black/5 rounded-lg hover:bg-black/10 transition-colors min-w-[200px]"
          >
            <div className="w-10 h-10 bg-[#7F66FF] rounded-lg flex items-center justify-center">
              <File className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{message.file_name}</p>
              <p className="text-xs opacity-60">
                {message.file_size ? `${(message.file_size / 1024).toFixed(1)} KB • ` : ''}Document
              </p>
            </div>
          </a>
        );
      
      default:
        return <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap">{message.content}</p>;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-b from-purple-50 to-pink-50" data-testid="loading-chat">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse mx-auto flex items-center justify-center">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-[#F8F4FF]" data-testid="chat-page">
      {/* Fixed Header */}
      <header className="flex-shrink-0 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] px-4 py-3 flex items-center gap-3 shadow-lg z-20" data-testid="chat-header">
        <button 
          onClick={() => navigate('/matches')} 
          className="text-white hover:bg-white/20 p-2 rounded-full transition-all active:scale-95" 
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        {otherUser && (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="relative flex-shrink-0">
              <img
                src={otherUser.photos?.[0] || 'https://images.unsplash.com/photo-1658909835269-e76abd3ffb5d?w=100'}
                alt={otherUser.name}
                className="w-11 h-11 rounded-full object-cover border-2 border-white/40 shadow-sm"
              />
              {isOnline && (
                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-400 border-2 border-white rounded-full shadow-sm" data-testid="online-indicator"></div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-white text-base truncate" data-testid="chat-user-name">{otherUser.name}</h2>
              <p className="text-xs text-white/80 truncate">
                {isTyping ? (
                  <span className="flex items-center gap-1">
                    <span className="animate-pulse">typing</span>
                    <span className="flex gap-0.5">
                      <span className="w-1 h-1 bg-white/80 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                      <span className="w-1 h-1 bg-white/80 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                      <span className="w-1 h-1 bg-white/80 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                    </span>
                  </span>
                ) : isOnline ? 'Online' : 'Last seen recently'}
              </p>
            </div>
          </div>
        )}
        
        {/* Header action buttons - simplified */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button className="text-white/90 hover:text-white hover:bg-white/20 p-2.5 rounded-full transition-all active:scale-95">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
          <button className="text-white/90 hover:text-white hover:bg-white/20 p-2.5 rounded-full transition-all active:scale-95">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      </header>

      {/* Messages Area - scrollable */}
      <main 
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3 py-3" 
        data-testid="messages-container"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E9DFFF' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        <div className="max-w-3xl mx-auto space-y-2 pb-4">
          {messages.map((message, index) => {
            const isSent = message.sender_id === user.id;
            const showDate = index === 0 || 
              new Date(messages[index - 1]?.created_at).toDateString() !== new Date(message.created_at).toDateString();
            
            return (
              <React.Fragment key={message.id}>
                {showDate && (
                  <div className="flex justify-center my-4">
                    <span className="bg-white text-purple-600 text-xs px-4 py-1.5 rounded-full shadow-sm font-medium">
                      {new Date(message.created_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                )}
                <div
                  className={`flex ${isSent ? 'justify-end' : 'justify-start'} px-1`}
                  data-testid={`message-${message.id}`}
                >
                  <div
                    className={`max-w-[80%] sm:max-w-[70%] px-3.5 py-2 shadow-sm ${
                      isSent
                        ? 'bg-gradient-to-br from-purple-100 to-purple-50 rounded-2xl rounded-br-md'
                        : 'bg-white rounded-2xl rounded-bl-md'
                    }`}
                  >
                    {renderMessageContent(message)}
                    
                    <div className="flex items-center justify-end gap-1.5 mt-1">
                      <span className="text-[11px] text-gray-400 font-medium">
                        {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isSent && (
                        <CheckCheck className={`w-4 h-4 ${message.read ? 'text-purple-500' : 'text-gray-300'}`} />
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          
          {isTyping && (
            <div className="flex justify-start px-1" data-testid="typing-indicator">
              <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={imageInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleImageSelect}
      />
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,.doc,.docx,.txt"
        onChange={handleFileSelect}
      />

      {/* Input Footer - Fixed at bottom */}
      <footer className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-t border-purple-100 z-20">
        {/* Recording UI */}
        {isRecording ? (
          <div className="px-3 py-3 safe-area-bottom">
            <div className="max-w-3xl mx-auto flex items-center gap-3">
              <button 
                onClick={cancelRecording}
                className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-all active:scale-95"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex-1 flex items-center gap-3 bg-purple-50 rounded-full px-4 py-2.5">
                <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></div>
                
                <div className="flex-1 flex items-center justify-center gap-0.5 h-6 overflow-hidden">
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 bg-gradient-to-t from-purple-500 to-pink-500 rounded-full transition-all"
                      style={{ height: `${Math.sin(i * 0.5 + recordingTime) * 8 + 12}px` }}
                    />
                  ))}
                </div>
                
                <span className="text-purple-600 font-semibold text-sm tabular-nums">
                  {formatRecordingTime(recordingTime)}
                </span>
              </div>
              
              <button 
                onClick={stopRecording}
                className="w-11 h-11 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : (
          /* Message Input Bar */
          <div className="px-3 py-2.5 safe-area-bottom" data-testid="message-form">
            <div className="max-w-3xl mx-auto flex items-center gap-2">
              {/* Emoji & Attachment combined */}
              <div className="flex items-center">
                <div className="relative">
                  <button
                    type="button"
                    className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-purple-500 rounded-full hover:bg-purple-50 transition-all active:scale-95"
                    onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowAttachMenu(false); }}
                    data-testid="emoji-button"
                  >
                    <Smile className="w-5 h-5" />
                  </button>
                  
                  {showEmojiPicker && (
                    <div className="absolute bottom-12 left-0 z-30 shadow-2xl rounded-xl overflow-hidden">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          setNewMessage(prev => prev + emojiData.emoji);
                          setShowEmojiPicker(false);
                        }}
                        width={300}
                        height={350}
                        searchPlaceholder="Search emoji..."
                        previewConfig={{ showPreview: false }}
                      />
                    </div>
                  )}
                </div>
                
                <div className="relative">
                  <button
                    type="button"
                    className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-purple-500 rounded-full hover:bg-purple-50 transition-all active:scale-95"
                    onClick={() => { setShowAttachMenu(!showAttachMenu); setShowEmojiPicker(false); }}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                    ) : (
                      <Paperclip className="w-5 h-5" />
                    )}
                  </button>
                  
                  {showAttachMenu && (
                    <div className="absolute bottom-12 left-0 bg-white rounded-2xl shadow-2xl p-4 z-30 border border-gray-100" style={{ width: '240px' }}>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          className="flex flex-col items-center gap-2 p-2 hover:bg-purple-50 rounded-xl transition-colors"
                          onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false); }}
                        >
                          <div className="w-11 h-11 bg-purple-500 rounded-full flex items-center justify-center shadow-md">
                            <File className="w-5 h-5 text-white" />
                          </div>
                          <span className="text-xs text-gray-600 font-medium">File</span>
                        </button>
                        <button
                          type="button"
                          className="flex flex-col items-center gap-2 p-2 hover:bg-purple-50 rounded-xl transition-colors"
                          onClick={() => { imageInputRef.current?.click(); setShowAttachMenu(false); }}
                        >
                          <div className="w-11 h-11 bg-pink-500 rounded-full flex items-center justify-center shadow-md">
                            <Image className="w-5 h-5 text-white" />
                          </div>
                          <span className="text-xs text-gray-600 font-medium">Photo</span>
                        </button>
                        <button
                          type="button"
                          className="flex flex-col items-center gap-2 p-2 hover:bg-purple-50 rounded-xl transition-colors"
                          onClick={() => { startRecording(); setShowAttachMenu(false); }}
                        >
                          <div className="w-11 h-11 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-md">
                            <Mic className="w-5 h-5 text-white" />
                          </div>
                          <span className="text-xs text-gray-600 font-medium">Audio</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Text input */}
              <form onSubmit={sendMessage} className="flex-1">
                <input
                  type="text"
                  value={newMessage}
                  onChange={handleTyping}
                  placeholder="Type a message..."
                  className="w-full bg-gray-100 rounded-full px-4 py-2.5 text-[15px] outline-none focus:bg-white focus:ring-2 focus:ring-purple-200 transition-all"
                  data-testid="message-input"
                  onFocus={() => { setShowEmojiPicker(false); setShowAttachMenu(false); }}
                />
              </form>
              
              {/* Send/Mic button */}
              <button 
                onClick={newMessage.trim() ? sendMessage : startRecording}
                className="w-11 h-11 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all active:scale-95 flex-shrink-0"
                data-testid={newMessage.trim() ? "send-button" : "mic-button"}
              >
                {newMessage.trim() ? <Send className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            </div>
          </div>
        )}
      </footer>
    </div>
  );
};